/* Fragrance Discovery Set — "Welcome to the neighborhood"
 * Runtime for sections/pdp-discovery-house.liquid (markup is rendered by Liquid).
 *
 * Scroll decides WHICH pouch is active and whether it is in its hero pose or
 * placed; the wiggles are time-based CSS keyframes (steps()), so the stop-motion
 * cadence never depends on scroll speed and every state is reversible.
 *
 * Pattern: native position:sticky track/pin (sections/impact-comparison.liquid),
 * progress from getBoundingClientRect in a rAF gated on inView, discrete
 * scroll -> state machine with hysteresis, DOM writes only on state change.
 */
(function () {
  'use strict';

  function House(root) {
    this.root = root;
    if (/[?&]fds-debug\b/.test(window.location.search)) root.classList.add('fds-house--debug');
    var cfgNode = root.querySelector('script[data-fds-config]');
    var cfg = { count: 0, introSeg: 0.4, outroSeg: 1.0, heroShare: 0.55, hysteresis: 0.015 };
    try { if (cfgNode) cfg = Object.assign(cfg, JSON.parse(cfgNode.textContent)); } catch (e) { /* keep defaults */ }
    this.cfg = cfg;
    this.track = root.querySelector('[data-fds-track]');
    this.pin = root.querySelector('[data-fds-pin]');
    this.heroBox = root.querySelector('[data-fds-hero]');
    this.heroInner = root.querySelector('[data-fds-hero-inner]');
    this.anchors = Array.prototype.slice.call(root.querySelectorAll('[data-fds-anchor]'));
    this.pouches = Array.prototype.slice.call(root.querySelectorAll('[data-fds-pouch]'));
    this.cardIntro = root.querySelector('[data-fds-card="intro"]');
    this.cardDone = root.querySelector('[data-fds-card="done"]');
    this.infoCards = Array.prototype.slice.call(root.querySelectorAll('[data-fds-card="info"]'));
    this.N = Math.min(this.pouches.length, this.anchors.length, this.infoCards.length);
    this.lastKey = null; this.prev = { i: -1, phase: null }; this.raf = null; this.inView = false;
    this.initialised = false; this.layoutSize = null; this.heroShown = -1; this.listeners = [];

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.track || !this.pin || !this.heroBox || this.N === 0 || reduce) {
      root.classList.add('fds-house--static');
      this.pouches.forEach(function (p) { p.className = 'fds-house__pouch is-placed'; });
      return;
    }
    this.armed = true;

    // Set up ~2 viewports early so it is ready before it is reached; nothing runs for visitors who never scroll this far.
    var self = this;
    if ('IntersectionObserver' in window) {
      this.armIO = new IntersectionObserver(function (entries, obs) {
        if (!entries[0].isIntersecting) return;
        obs.disconnect(); self.armIO = null; self.init();
      }, { rootMargin: '200% 0px' });
      this.armIO.observe(root);
    } else { this.init(); }
  }

  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* The pinned stage sits under the sticky header and, on mobile, above the sticky ATC bar; measure both so the
     pin height is exactly the visible space. The header group holds the announcement bar too (static, 0px when
     hidden), so pick the header-group section that is actually sticky/fixed rather than the first one in the DOM.
     The ATC element is fixed on desktop as well, but there it is a ~300px box at the right edge that never sits
     under the stage content, so it only counts when it spans the viewport (the full-width mobile bar). */
  House.prototype.measureChrome = function () {
    var header = document.querySelector('.section-header-eb');
    if (!header || !/sticky|fixed/.test(getComputedStyle(header).position)) {
      header = Array.prototype.filter.call(document.querySelectorAll('.shopify-section-group-header-group'), function (el) {
        return /sticky|fixed/.test(getComputedStyle(el).position);
      })[0] || null;
    }
    var top = header ? Math.round(header.getBoundingClientRect().height) : 64;
    var atc = document.body.classList.contains('pdp-has-sticky-atc') ? document.querySelector('.pdp-sticky-atc') : null;
    var bottom = 0;
    if (atc) {
      var ar = atc.getBoundingClientRect();
      if (ar.width >= window.innerWidth * 0.6) bottom = Math.round(ar.height);
    }
    this.root.style.setProperty('--fds-header', top + 'px');
    this.root.style.setProperty('--fds-bottom', bottom + 'px');
  };

  /* FLIP: one transform per pouch that carries it from its slot to the (upright) hero box.
     The translate runs inside the (possibly rotated) holder, so the screen delta is rotated
     into the holder's local frame; the pouch is counter-rotated so it reads upright as a hero. */
  House.prototype.layout = function () {
    this.measureChrome();
    var rot = parseFloat(getComputedStyle(this.root).getPropertyValue('--house-rot')) || 0;
    var th = rot * Math.PI / 180, c = Math.cos(th), s = Math.sin(th);
    var h = this.heroBox.getBoundingClientRect();
    var hcx = h.left + h.width / 2, hcy = h.top + h.height / 2;
    var pouches = this.pouches;
    this.anchors.forEach(function (a, k) {
      if (!pouches[k]) return;
      var r = a.getBoundingClientRect();
      var dx = hcx - (r.left + r.width / 2), dy = hcy - (r.top + r.height / 2);
      var lx = dx * c + dy * s, ly = -dx * s + dy * c;
      var scale = h.width / (a.offsetWidth || 1);
      pouches[k].style.setProperty('--hero-transform', 'translate(' + lx.toFixed(1) + 'px,' + ly.toFixed(1) + 'px) rotate(' + (-rot) + 'deg) scale(' + scale.toFixed(4) + ')');
    });
    this.layoutSize = [this.pin.offsetWidth, this.pin.offsetHeight];
  };

  House.prototype.progress = function () {
    var stickyTop = parseFloat(getComputedStyle(this.pin).top) || 0;
    var r = this.track.getBoundingClientRect();
    var span = this.track.offsetHeight - this.pin.offsetHeight;
    return span > 0 ? clamp((stickyTop - r.top) / span, 0, 1) : 1;   // 0 = pin locks, 1 = releases
  };

  House.prototype.stateFor = function (s) {
    var cfg = this.cfg, N = this.N;
    var seg = s * (cfg.introSeg + N + cfg.outroSeg);
    if (seg < cfg.introSeg) return { stage: 'intro', i: -1, phase: null };
    if (seg >= cfg.introSeg + N) return { stage: 'done', i: N, phase: null };
    var t = seg - cfg.introSeg, i = Math.min(N - 1, Math.floor(t)), f = t - i;
    var edge = cfg.heroShare + (this.prev.i === i && this.prev.phase === 'placed' ? -cfg.hysteresis : cfg.hysteresis);
    return { stage: 'pouch', i: i, phase: f < edge ? 'hero' : 'placed' };
  };

  function setClass(node, cls) { if (node.className !== cls) node.className = cls; }

  House.prototype.apply = function (st) {
    var self = this;
    this.pouches.forEach(function (p, k) {
      var cls;
      if (st.stage === 'intro') cls = 'fds-house__pouch is-waiting';
      else if (st.stage === 'done' || k < st.i) cls = 'fds-house__pouch is-placed';
      else if (k === st.i) cls = st.phase === 'hero' ? 'fds-house__pouch is-waiting' : 'fds-house__pouch is-placed is-latest';
      else cls = 'fds-house__pouch is-waiting';
      setClass(p, cls);
    });
    var active = st.stage === 'intro' ? this.cardIntro : st.stage === 'done' ? this.cardDone : this.infoCards[st.i];
    [this.cardIntro, this.cardDone].concat(this.infoCards).forEach(function (c) { if (c) c.classList.toggle('is-active', c === active); });
    var heroKey = st.stage === 'pouch' && st.phase === 'hero' ? st.i : -1;
    if (heroKey !== this.heroShown && this.heroInner) {
      this.heroInner.innerHTML = '';
      if (heroKey >= 0) {
        var src = this.pouches[heroKey].getAttribute('data-fds-hero-src');
        if (src) {
          var hi = document.createElement('img');
          hi.className = 'fds-house__hero-img'; hi.alt = ''; hi.decoding = 'async'; hi.src = src; hi.width = 800; hi.height = 1056;
          this.heroInner.appendChild(hi);
        }
      }
      this.heroShown = heroKey;
    }
    this.root.setAttribute('data-stage', st.stage);
    this.prev = { i: st.i, phase: st.phase };
    void self;
  };

  House.prototype.render = function () {
    var st = this.stateFor(this.progress());
    var key = st.stage + ':' + st.i + ':' + st.phase;
    if (key !== this.lastKey) { this.apply(st); this.lastKey = key; }
    var self = this;
    this.raf = this.inView ? window.requestAnimationFrame(function () { self.render(); }) : null;
  };

  House.prototype.ensureRunning = function () {
    var self = this;
    if (this.raf == null) this.raf = window.requestAnimationFrame(function () { self.render(); });
  };

  House.prototype.on = function (target, type, fn, opts) { target.addEventListener(type, fn, opts); this.listeners.push([target, type, fn, opts]); };

  House.prototype.init = function () {
    if (this.initialised) return; this.initialised = true;
    var self = this;
    this.layout();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.layout(); self.lastKey = null; self.ensureRunning(); });
    this.io = new IntersectionObserver(function (entries) {
      self.inView = entries.some(function (e) { return e.isIntersecting; });
      if (self.inView) self.ensureRunning();
    }, { threshold: 0 });
    this.io.observe(this.root);
    // Backstop: a fast scroll or programmatic jump can outrun the observer; the loop stops
    // itself when off-screen, so this only ever re-enables it.
    this.on(window, 'scroll', function () { self.ensureRunning(); }, { passive: true });
    var t;
    var onResize = function () {
      clearTimeout(t);
      t = setTimeout(function () {
        self.measureChrome();
        if (self.layoutSize && self.layoutSize[0] === self.pin.offsetWidth && self.layoutSize[1] === self.pin.offsetHeight) return;   // iOS address-bar noise
        self.root.classList.add('is-relayout');
        self.layout(); self.lastKey = null; self.apply(self.stateFor(self.progress()));
        requestAnimationFrame(function () { requestAnimationFrame(function () { self.root.classList.remove('is-relayout'); }); });
      }, 150);
    };
    this.on(window, 'resize', onResize);
    this.on(window, 'orientationchange', onResize);
    // The sticky ATC bar mounts after this script (it lives in the product section); re-measure once it exists.
    setTimeout(function () { self.measureChrome(); }, 1200);
    this.apply(this.stateFor(this.progress())); this.ensureRunning();
  };

  /* Theme editor: clicking a pouch block scrolls the page to that pouch's hero pose. */
  House.prototype.scrollToPouch = function (k) {
    if (!this.initialised || k < 0 || k >= this.N) return;
    var cfg = this.cfg;
    var target = (cfg.introSeg + k + cfg.heroShare * 0.5) / (cfg.introSeg + this.N + cfg.outroSeg);
    var stickyTop = parseFloat(getComputedStyle(this.pin).top) || 0;
    var span = this.track.offsetHeight - this.pin.offsetHeight;
    var y = this.track.getBoundingClientRect().top + window.scrollY - stickyTop + span * target;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  House.prototype.destroy = function () {
    if (this.armIO) { this.armIO.disconnect(); this.armIO = null; }
    if (this.io) { this.io.disconnect(); this.io = null; }
    if (this.raf != null) { window.cancelAnimationFrame(this.raf); this.raf = null; }
    this.inView = false;
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
  };

  /* ── Boot ─────────────────────────────────────────────────────────────── */
  function boot(scope) {
    var ctx = scope || document;
    var nodes = ctx.querySelectorAll('[data-fds-house]');
    Array.prototype.forEach.call(nodes, function (root) {
      if (root.__fdsHouse) return;
      root.__fdsHouse = new House(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var nodes = e.target.querySelectorAll('[data-fds-house]');
    Array.prototype.forEach.call(nodes, function (root) {
      if (root.__fdsHouse) { root.__fdsHouse.destroy(); root.__fdsHouse = null; }
    });
  });
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest && e.target.closest('[data-fds-house]');
    if (!root || !root.__fdsHouse) return;
    var k = parseInt(e.target.getAttribute('data-fds-index'), 10);
    if (!isNaN(k)) root.__fdsHouse.scrollToPouch(k);
  });
})();
